// AWS Configuration for the browser bundle of `public/aws-config.json`.
// Production deploy strips access keys (see deploy.sh); uploads use API presigned URLs + Cognito.
// Local/dev may include accessKeyId + secretAccessKey for direct S3/Dynamo SDK paths.

interface AwsConfig {
  accessKeyId: string;
  secretAccessKey: string;
  region: string;
  s3Bucket: string;
  dynamoDBTable: string;
}

let currentConfig: AwsConfig = {
  accessKeyId: '',
  secretAccessKey: '',
  region: '',
  s3Bucket: '',
  dynamoDBTable: '',
};

/** True when root aws-config.json included IAM keys (local direct AWS SDK use). Never true in shipped production JSON. */
export const hasBrowserAwsCredentials = (): boolean =>
  !!currentConfig.accessKeyId && !!currentConfig.secretAccessKey;

const loadAwsConfig = async (): Promise<void> => {
  try {
    const response = await fetch('/aws-config.json');
    if (!response.ok) {
      throw new Error(`Failed to load aws-config.json: ${response.status} ${response.statusText}`);
    }
    const config = await response.json();

    if (!config.region || !config.s3Bucket || !config.dynamoDBTable) {
      throw new Error(
        'Missing required fields in aws-config.json: region, s3Bucket, dynamoDBTable'
      );
    }

    const hasKeyId = !!config.accessKeyId;
    const hasSecret = !!config.secretAccessKey;
    if (hasKeyId !== hasSecret) {
      throw new Error(
        'Invalid aws-config.json: provide both accessKeyId and secretAccessKey for local dev, or omit both for production.'
      );
    }

    currentConfig = {
      accessKeyId: typeof config.accessKeyId === 'string' ? config.accessKeyId : '',
      secretAccessKey: typeof config.secretAccessKey === 'string' ? config.secretAccessKey : '',
      region: config.region,
      s3Bucket: config.s3Bucket,
      dynamoDBTable: config.dynamoDBTable,
    };

    if (hasBrowserAwsCredentials()) {
      console.log('AWS config loaded from aws-config.json (direct credentials — local/dev only).');
    } else {
      console.log(
        'AWS config loaded from aws-config.json (no keys in bundle — production; use API + presigned URLs).'
      );
    }
  } catch (error) {
    console.error('❌ ERROR: Failed to load AWS configuration from aws-config.json:', error);
    console.error(
      '   Ensure aws-config.json exists under public/ with region, s3Bucket, dynamoDBTable (and optional keys for local dev).'
    );
    throw error;
  }
};

loadAwsConfig().catch((error) => {
  console.error('Fatal: Cannot proceed without aws-config.json');
});

export const awsCredentials = {
  get accessKeyId() {
    return currentConfig.accessKeyId;
  },
  get secretAccessKey() {
    return currentConfig.secretAccessKey;
  },
  get region() {
    return currentConfig.region;
  },
};

export const awsServices = {
  get s3Bucket() {
    return currentConfig.s3Bucket;
  },
  get dynamoDBTable() {
    return currentConfig.dynamoDBTable;
  },
};
